/**
 * CRA/Vite: `REACT_APP_API_BASE_URL` is local/dev only. Production storefront uses
 * relative `/api/*` (empty base) plus the Vercel adapter's RASOIX_API_ORIGIN.
 * Next.js: `NEXT_PUBLIC_API_URL` — listed for shared env docs; CRA does not expose it unless defined.
 *
 * Same-origin mode (`REACT_APP_API_SAME_ORIGIN=1`, used by the CUSTOM_HOSTNAME QA ingress
 * harness) forces a relative `/api/...` base so the browser never calls the upstream API
 * host directly — the local dev proxy performs the server-to-server hop and adds
 * `X-Forwarded-Host`.
 */
import { DEV_DEFAULT_API_BASE_URL } from "@/config/dev-api-origin";

export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  ""
).trim();

let missingApiUrlLogged = false;

function normalizeApiBaseUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  // Users sometimes paste Swagger UI URL; API base should be the site origin only.
  const withoutSwagger = trimmed.replace(/\/swagger(?:\/.*)?$/i, "");

  try {
    const u = new URL(withoutSwagger);
    return `${u.protocol}//${u.host}`;
  } catch {
    return withoutSwagger.replace(/\/+$/, "");
  }
}

/** Explicit opt-in to relative `/api/...` requests served by a local/edge same-origin proxy. */
export function isSameOriginApiMode(): boolean {
  const raw = (process.env.REACT_APP_API_SAME_ORIGIN ?? "").trim().toLowerCase();
  return raw === "1" || raw === "true";
}

function currentBrowserHostname(): string {
  if (typeof window === "undefined" || !window.location) return "";
  return (window.location.hostname ?? "").trim().toLowerCase();
}

/**
 * True for loopback dev origins where the bundled dev default API host is acceptable.
 * A non-loopback hostname means the app was entered through a custom hostname, where a
 * direct browser call to the upstream API host would bypass the trusted proxy.
 */
function isLoopbackDevOrigin(hostname: string): boolean {
  return (
    hostname === "" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]"
  );
}

/**
 * API origin for HTTP clients. Empty string means same-origin (`/api/...`).
 */
export function getRequiredApiBaseUrl(): string {
  if (isSameOriginApiMode()) return "";

  const fromEnv = normalizeApiBaseUrl(API_BASE_URL);

  if (process.env.NODE_ENV !== "production") {
    if (fromEnv) return fromEnv;

    if (!isLoopbackDevOrigin(currentBrowserHostname())) {
      // CUSTOM_HOSTNAME entry: never send browser traffic straight to the bundled API host.
      return "";
    }

    if (typeof console !== "undefined" && console.error) {
      console.error(
        "[config] API URL not configured — using bundled dev default. Set REACT_APP_API_BASE_URL in .env.local (HTTPS origin only, no /swagger)."
      );
    }
    // Local CRA loopback only. Must stay inside this non-production branch so
    // the hostname is not emitted into the production bundle.
    return normalizeApiBaseUrl(DEV_DEFAULT_API_BASE_URL);
  }

  if (fromEnv) return fromEnv;

  if (typeof console !== "undefined" && console.error && !missingApiUrlLogged) {
    missingApiUrlLogged = true;
    console.error(
      "[config] REACT_APP_API_BASE_URL is not set. Configure it in the host environment (e.g. Vercel Project → Environment Variables)."
    );
  }
  return "";
}

/**
 * Origin for static media served by the API host (images).
 * Kept absolute in dev so the QA ingress harness (which proxies only `/api`) still renders images.
 */
export function getAssetBaseUrl(): string {
  const fromEnv = normalizeApiBaseUrl(API_BASE_URL);
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV !== "production") {
    // Local CRA media convenience only — not a production API origin.
    return normalizeApiBaseUrl(DEV_DEFAULT_API_BASE_URL);
  }
  return "";
}
